import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

export interface ValueRow {
  label: string;
  value: ReactNode;
  /** Shown to the right of the value, e.g. a derived age or an override marker. */
  note?: ReactNode;
  /** Identifier face for transcribed digit strings. */
  identifier?: boolean;
  /** Arabic text: set in Plex Arabic and right-aligned. */
  arabic?: boolean;
  /** Right-align as a figure so a column of numbers lines up. */
  figure?: boolean;
}

export interface ValueListProps {
  rows: readonly ValueRow[];
  /** Two columns on wide screens for long record sections. */
  columns?: 1 | 2;
  className?: string;
}

const EMPTY = <span className="text-fg-placeholder">Not given</span>;

/**
 * Label and value pairs for a read-only record.
 *
 * Labels are sentence case in a smaller, muted style; the value carries the
 * weight. Rows are separated by a faint rule rather than boxed, so a long
 * record reads as one continuous document inside its panel.
 */
export function ValueList({ rows, columns = 1, className }: ValueListProps) {
  return (
    <dl
      className={cn(
        "grid",
        columns === 2 ? "sm:grid-cols-2 sm:gap-x-10" : "grid-cols-1",
        className,
      )}
    >
      {rows.map((row, index) => (
        <div
          // Labels repeat within a section: a record shows "Expires" under both
          // the Emirates ID and the passport.
          key={`${index}-${row.label}`}
          className="flex items-baseline justify-between gap-6 border-b border-line-faint py-2.5 last:border-b-0"
        >
          <dt className="shrink-0 text-xs text-fg-subtle">{row.label}</dt>
          <dd
            className={cn(
              "min-w-0 text-right text-base font-medium text-fg",
              // Only a real value gets the identifier face; the placeholder
              // stays in the body face so an empty row does not read as data.
              row.identifier && row.value != null && row.value !== "" && "u-ident",
              row.figure && "u-figure",
            )}
          >
            {row.arabic && row.value ? (
              <span lang="ar" dir="rtl" className="u-arabic">
                {row.value}
              </span>
            ) : (
              (row.value ?? EMPTY)
            )}
            {row.note ? (
              <span className="ml-2 text-xs text-fg-subtle">{row.note}</span>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}
